import React from "react";
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import SwipeLeftOutlinedIcon from "@mui/icons-material/SwipeLeftOutlined";
import {
  getPedidoProfitMeta,
  formatPedidoAmount,
  formatProfitPct,
  PROFIT_LEGEND,
  TIPOCONT_LEGEND,
  getPedidoDocKindMeta,
  getPedidoTipocontMeta,
  getPedidoPendingAmount,
  getPedidoDisplayUnits,
  getPedidoDisplayInvSales,
  formatPedidoCurrency,
  rowMatchesPedidoFocus,
} from "./pedidosVentaUtils";
import ClienteAvatar, { RepAvatar } from "./ClienteAvatar";

const GROUPS = [
  { key: "pedido", label: "Pedido", span: 6, colorKey: "info" },
  { key: "uds", label: "Unidades", span: 5, colorKey: "neutral" },
  { key: "compra", label: "Compra", span: 3, colorKey: "purchase" },
  { key: "venta", label: "Venta", span: 3, colorKey: "sales" },
  { key: "margen", label: "Margen", span: 2, colorKey: "profit" },
  { key: "estado", label: "Estado", span: 2, colorKey: "neutral" },
];

function groupTone(theme, colorKey) {
  const isDark = theme.palette.mode === "dark";
  const paper = theme.palette.background.paper;
  const map = {
    info: {
      bg: isDark ? "#1e2d36" : "#dceef5",
      border: isDark ? theme.palette.info.light : theme.palette.info.main,
      text: isDark ? theme.palette.info.light : theme.palette.info.dark,
    },
    neutral: {
      bg: isDark ? "#2a2a2e" : "#eceff3",
      border: theme.palette.divider,
      text: theme.palette.text.secondary,
    },
    purchase: {
      bg: isDark ? "#352628" : "#f8e4e4",
      border: isDark ? theme.palette.error.light : theme.palette.error.main,
      text: isDark ? theme.palette.error.light : theme.palette.error.dark,
    },
    sales: {
      bg: isDark ? "#1e2838" : "#e3ebf8",
      border: isDark ? theme.palette.primary.light : theme.palette.primary.main,
      text: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
    },
    profit: {
      bg: isDark ? "#1f3028" : "#e2f2e6",
      border: isDark ? theme.palette.success.light : theme.palette.success.main,
      text: isDark ? theme.palette.success.light : theme.palette.success.dark,
    },
  };
  const tone = map[colorKey] || map.neutral;
  return { ...tone, bg: tone.bg || paper };
}

function colHeaderBg(theme, zone) {
  const isDark = theme.palette.mode === "dark";
  const map = {
    neutral: isDark ? "#2a2a2e" : "#f3f4f6",
    purchase: isDark ? "#352628" : "#faecec",
    sales: isDark ? "#1e2838" : "#e8effa",
    profit: isDark ? "#1f3028" : "#e6f4ea",
    invoicePurchase: isDark ? "#3a2a2c" : "#f5e8e8",
    invoiceSales: isDark ? "#243040" : "#e6edf8",
  };
  return map[zone] || map.neutral;
}

function colBg(theme, zone) {
  const zones = {
    purchase: alpha(theme.palette.error.main, 0.055),
    sales: alpha(theme.palette.primary.main, 0.05),
    profit: alpha(theme.palette.success.main, 0.06),
    invoicePurchase: alpha(theme.palette.error.main, 0.08),
    invoiceSales: alpha(theme.palette.primary.main, 0.07),
  };
  return zones[zone];
}

const GROUP_HEADER_H = 40;
const COL_HEADER_H = 36;

const headerBase = {
  fontWeight: 800,
  fontSize: "0.68rem",
  letterSpacing: 0.35,
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  borderBottom: "1px solid",
  borderColor: "divider",
  boxSizing: "border-box",
};

const stickyHeadCell = (top, zIndex) => ({
  position: "sticky",
  top,
  zIndex,
  backgroundClip: "padding-box",
});

const PedidosVentaTableDesktop = ({
  theme,
  accent,
  rows,
  focus = null,
  onFocusRow,
  onOpenDoc,
}) => {
  const stickyCustomerSx = {
    position: "sticky",
    left: 0,
    zIndex: 3,
    minWidth: 84,
    maxWidth: 96,
    width: 88,
    bgcolor: theme.palette.background.paper,
    boxShadow: `4px 0 10px ${alpha(theme.palette.common.black, 0.06)}`,
    borderRight: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.85),
  };

  const bodyStickySx = {
    ...stickyCustomerSx,
    zIndex: 2,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const renderAmount = (val, { negativeRed = true } = {}) => (
    <Typography
      component="span"
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: 600,
        fontSize: "0.74rem",
        color:
          negativeRed && Number(val) < 0 ? theme.palette.error.main : "inherit",
      }}
    >
      {formatPedidoAmount(val, { decimals: 2 }) || ""}
    </Typography>
  );

  return (
    <Box sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.25, sm: 2 },
          py: 1,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.default, 0.45),
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <SwipeLeftOutlinedIcon
            sx={{ fontSize: 17, color: "text.secondary" }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {rows.length} líneas · avatares = cliente y AM · clic en el PO abre
            el resumen
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
          {TIPOCONT_LEGEND.map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={item.label}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.66rem",
                color: item.color,
                bgcolor: alpha(item.color, 0.12),
                border: "1px solid",
                borderColor: alpha(item.color, 0.35),
              }}
            />
          ))}
          {PROFIT_LEGEND.map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={item.label}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.66rem",
                color: item.color,
                bgcolor: alpha(item.color, 0.12),
                border: "1px solid",
                borderColor: alpha(item.color, 0.35),
              }}
            />
          ))}
        </Stack>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          overflow: "auto",
          maxHeight: "calc(100dvh - 268px)",
          width: "100%",
          borderRadius: 0,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 1380,
            borderCollapse: "separate",
            borderSpacing: 0,
            "& .MuiTableCell-root": {
              py: 0.55,
              px: 0.85,
              borderBottom: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.55),
              fontSize: "0.74rem",
            },
            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
              bgcolor: alpha(theme.palette.background.default, 0.32),
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              bgcolor: alpha(accent, 0.06),
            },
          }}
        >
          <TableHead
            sx={{
              "& .MuiTableCell-head": {
                backgroundImage: "none",
                py: "0 !important",
              },
            }}
          >
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  ...headerBase,
                  ...stickyCustomerSx,
                  ...stickyHeadCell(0, 7),
                  left: 0,
                  height: GROUP_HEADER_H + COL_HEADER_H,
                  minHeight: GROUP_HEADER_H + COL_HEADER_H,
                  py: 0,
                  verticalAlign: "middle",
                  bgcolor:
                    theme.palette.mode === "dark" ? "#1e2838" : "#dce8f5",
                  color: "primary.dark",
                  borderTop: `3px solid ${accent}`,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  boxShadow: `0 2px 0 0 ${theme.palette.mode === "dark" ? "#1e2838" : "#dce8f5"}`,
                  textAlign: "center",
                  px: 0.5,
                }}
              >
                Cli
              </TableCell>
              {GROUPS.map((g) => {
                const tone = groupTone(theme, g.colorKey);
                return (
                  <TableCell
                    key={g.key}
                    colSpan={g.span}
                    align="center"
                    sx={{
                      ...headerBase,
                      ...stickyHeadCell(0, 5),
                      height: GROUP_HEADER_H,
                      minHeight: GROUP_HEADER_H,
                      maxHeight: GROUP_HEADER_H,
                      py: 0,
                      lineHeight: `${GROUP_HEADER_H}px`,
                      bgcolor: tone.bg,
                      color: tone.text,
                      borderTop: `3px solid ${tone.border}`,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    {g.label}
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              {[
                "Purch. Doc",
                "PO Date",
                "Delivery",
                "Article",
                "Type",
                "Style",
                "Un",
                "Served",
                "Cancelled",
                "Ptes · €",
                "Curr.",
                "U.Cs.€",
                "Purch. €",
                "Prch Inv.",
                "U.Pr.€",
                "Sales €",
                "Sl.Inv. €",
                "Profit",
                "% Pr €",
                "Sit.",
                "Tipo",
              ].map((label, idx) => {
                let zone = "neutral";
                if (idx >= 11 && idx <= 12) zone = "purchase";
                else if (idx === 13) zone = "invoicePurchase";
                else if (idx >= 14 && idx <= 15) zone = "sales";
                else if (idx === 16) zone = "invoiceSales";
                else if (idx >= 17 && idx <= 18) zone = "profit";

                return (
                  <TableCell
                    key={label}
                    align={idx >= 11 ? "right" : "left"}
                    sx={{
                      ...headerBase,
                      ...stickyHeadCell(GROUP_HEADER_H, 4),
                      height: COL_HEADER_H,
                      minHeight: COL_HEADER_H,
                      maxHeight: COL_HEADER_H,
                      py: 0,
                      lineHeight: `${COL_HEADER_H}px`,
                      bgcolor: colHeaderBg(theme, zone),
                      fontSize: "0.64rem",
                      boxShadow: `0 2px 0 0 ${colHeaderBg(theme, zone)}`,
                    }}
                  >
                    {label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const profit = getPedidoProfitMeta(row);
              const docKind = getPedidoDocKindMeta(row);
              const tipocontMeta = getPedidoTipocontMeta(row);
              const units = getPedidoDisplayUnits(row);
              const invSales = getPedidoDisplayInvSales(row);
              const pending = units.ptes;
              const cancelled = units.anul;
              const highlighted = rowMatchesPedidoFocus(row, focus);
              const dimmed = Boolean(focus) && !highlighted;
              const isParte = tipocontMeta.isParte;
              const rowBg = highlighted
                ? alpha(accent, theme.palette.mode === "dark" ? 0.22 : 0.14)
                : i % 2 === 1
                  ? theme.palette.mode === "dark"
                    ? "#2a2a2e"
                    : "#f3f4f6"
                  : theme.palette.background.paper;

              return (
                <TableRow
                  key={`${row.CLIPRO || ""}-${row.PorchDoc || "r"}-${row.CODART || ""}-${row.TIPOCONT || ""}-${i}`}
                  hover
                  onClick={() => onOpenDoc?.(row)}
                  sx={{
                    cursor: "pointer",
                    opacity: dimmed ? 0.38 : isParte ? 0.72 : 1,
                    transition:
                      "opacity 0.15s ease, background-color 0.15s ease",
                    outline: highlighted
                      ? `2px solid ${alpha(accent, 0.45)}`
                      : "none",
                    outlineOffset: -2,
                    borderLeft: isParte
                      ? `3px solid ${alpha(tipocontMeta.color, 0.65)}`
                      : "3px solid transparent",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                >
                  <TableCell
                    align="center"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      ...bodyStickySx,
                      bgcolor: rowBg,
                      px: 0.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.4}
                      justifyContent="center"
                    >
                      <ClienteAvatar
                        row={row}
                        size={30}
                        onPaintCliente={(item) => onFocusRow?.("cliente", item)}
                      />
                      <RepAvatar row={row} size={30} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Clic: abrir resumen del PO">
                        <span>{row.PorchDoc}</span>
                      </Tooltip>
                      <Chip
                        size="small"
                        label={tipocontMeta.label}
                        sx={{
                          height: 18,
                          fontWeight: 800,
                          fontSize: "0.58rem",
                          color: tipocontMeta.color,
                          bgcolor: alpha(tipocontMeta.color, 0.12),
                          border: "1px solid",
                          borderColor: alpha(tipocontMeta.color, 0.35),
                          "& .MuiChip-label": { px: 0.55 },
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: "nowrap", color: "text.secondary" }}
                  >
                    {row.FECHA ? dayjs(row.FECHA).format("DD/MM/YY") : ""}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: "nowrap", color: "text.secondary" }}
                  >
                    {row.DeliveryDate
                      ? dayjs(row.DeliveryDate).format("DD/MM/YY")
                      : ""}
                  </TableCell>
                  <TableCell>{row.CODART}</TableCell>
                  <TableCell>{row.ARTALIAS}</TableCell>
                  <TableCell>{row.CAR1}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {units.un}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {units.serv}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: cancelled > 0 ? 800 : 500,
                      color: cancelled > 0 ? "error.main" : "text.primary",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {units.anul}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: pending > 0 ? 800 : 500,
                      color: pending > 0 ? "error.main" : "text.primary",
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box>
                      {units.ptes}
                      {pending > 0 ? (
                        <Typography
                          component="span"
                          display="block"
                          sx={{
                            fontSize: "0.62rem",
                            fontWeight: 800,
                            color: "error.dark",
                            lineHeight: 1.15,
                          }}
                        >
                          {formatPedidoAmount(getPedidoPendingAmount(row)) ||
                            "0,00"}{" "}
                          €
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <Tooltip title="Moneda del pedido en A3 · importes de venta ya en €">
                      <span>{formatPedidoCurrency(row)}</span>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: colBg(theme, "purchase"),
                      borderLeft: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {renderAmount(row.UNPedCompraE)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ bgcolor: colBg(theme, "purchase") }}
                  >
                    {renderAmount(row.PedidoCompraE)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ bgcolor: colBg(theme, "invoicePurchase") }}
                  >
                    {renderAmount(row.InvCostMoneda)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: colBg(theme, "sales"),
                      borderLeft: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {renderAmount(row.UNPedVentaE, { negativeRed: false })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ bgcolor: colBg(theme, "sales") }}
                  >
                    {renderAmount(row.VentaBaseEUR, { negativeRed: false })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ bgcolor: colBg(theme, "invoiceSales") }}
                  >
                    {renderAmount(invSales, { negativeRed: false })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: colBg(theme, "profit"),
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      fontWeight: 800,
                      color: profit.color,
                    }}
                  >
                    {formatPedidoAmount(profit.profitAbs, { decimals: 2 }) ||
                      "0,00"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: colBg(theme, "profit"),
                      fontWeight: 800,
                      color: profit.color,
                    }}
                  >
                    {formatProfitPct(profit.profitPct)}
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        size="small"
                        label={docKind.label}
                        sx={{
                          height: 20,
                          fontWeight: 800,
                          fontSize: "0.62rem",
                          color: docKind.color,
                          bgcolor: alpha(docKind.color, 0.14),
                          border: "1px solid",
                          borderColor: alpha(docKind.color, 0.35),
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                      {profit.type === "noCosts" ||
                      profit.type === "articulo" ||
                      profit.type === "directoInv" ? (
                        <Chip
                          size="small"
                          label={
                            profit.type === "articulo"
                              ? "Coste ficha"
                              : profit.type === "directoInv"
                                ? "Profit = fact. Directo"
                                : "Sin compra"
                          }
                          sx={{
                            height: 20,
                            fontWeight: 800,
                            fontSize: "0.62rem",
                            color: profit.color,
                            bgcolor: alpha(profit.color, 0.14),
                            border: "1px solid",
                            borderColor: alpha(profit.color, 0.35),
                            "& .MuiChip-label": { px: 0.7 },
                          }}
                        />
                      ) : null}
                      {row.SITUACION ? (
                        <Chip
                          size="small"
                          label={row.SITUACION}
                          sx={{
                            height: 20,
                            fontWeight: 800,
                            fontSize: "0.62rem",
                          }}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                  >
                    {tipocontMeta.short}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PedidosVentaTableDesktop;
